import { Injectable, effect, signal, Signal, WritableSignal } from '@angular/core';
import { AppstateService } from '../appstate.service';
import { DxDataGridComponent } from 'devextreme-angular';
import { exportDataGrid } from 'devextreme/excel_exporter';
import { Workbook } from 'devextreme-exceljs-fork';
import { saveAs } from 'file-saver';
import { DialogService } from '../dialog.service';

@Injectable(
  {
    providedIn: 'root'
  })

export class GridService {

  public FilterTextNavigationProjekt = signal('');
  public FilterTextNavigationArchiv = signal('');
  public Bezugsjahr = signal<number>(new Date().getFullYear());

  constructor(private appstateService: AppstateService, private dialogService: DialogService) {

    effect(() => {
      // Filtertext based on routing used in grid panel
      let filter = "";
      if (this.appstateService.navigationPlangebiet() !== 'ALLE') {
        filter = "Plangebiet = " + this.appstateService.navigationPlangebiet();
      }
      if (this.appstateService.navigationBereich() !== 'alle') {
        if (filter) filter += " | ";
        filter += "Bereich = " + this.appstateService.navigationBereich();
      }
      this.FilterTextNavigationArchiv.set(filter);

      if (this.appstateService.navigationBearbeitung() !== 'alle') {
        if (filter) filter += " | ";
        filter += "Bearbeitung = " + this.appstateService.navigationBearbeitung();
      }  
      this.FilterTextNavigationProjekt.set(filter);
    }, {allowSignalWrites: true});
  }

  captionVorjahr() {
    return 'bis ' + (this.Bezugsjahr() - 1);
  }

  captionJahr(index: number) {
    return (this.Bezugsjahr() + index);
  }

  captionJahrFF(index: number) {
    return (this.Bezugsjahr() + index) + ' ff.';
  }

  PrepareSpecialRows(e: any) {
    if (e.rowType === "data") {
      if (e.data['isSummary'] === true) {
        e.rowElement.classList.add("dx-row-summary");
      }
      if (e.data['isProportional'] === true && e.rowElement.children.length > 0) {
        e.rowElement.children[0].classList.add("dx-cell-indent");
      }
    }
  }

  public saveGridState(dataGrid: DxDataGridComponent, stateSignal: WritableSignal<string>): void {
    if (!dataGrid?.instance) return;

    const state = dataGrid.instance.state?.();
    if (!state) return;

    // Persist state in AppState (including column order/width, filterValue, etc.)
    stateSignal.set(JSON.stringify(state));
  }

  public loadGridState(dataGrid: DxDataGridComponent, stateSignal: Signal<string>): void {
    if (!dataGrid?.instance) return;

    const raw = stateSignal();
    if (!raw) return;

    try {
      const state = JSON.parse(raw);
      dataGrid.instance.state(state);
    } catch {
      // ignore invalid state
    }
  }

  public resetGridState(dataGrid: DxDataGridComponent, stateSignal: WritableSignal<string>): void {
    if (!dataGrid?.instance) return;

    dataGrid.instance.state(null);
    dataGrid.instance.clearFilter();
    dataGrid.instance.clearSorting();
    dataGrid.instance.clearSelection();
    
    // Persist state in AppState (including column order/width, filterValue, etc.)
    stateSignal.set("");
  }

  private getExcelCellText(value: unknown): string {
    if (value === null || value === undefined) {
      return '';
    }

    if (Array.isArray(value)) {
      return value.map((entry) => this.getExcelCellText(entry)).join(', ');
    }

    if (typeof value === 'object') {
      const cellValue = value as {
        text?: unknown;
        hyperlink?: unknown;
        richText?: Array<{ text?: unknown }>;
        result?: unknown;
      };

      if (Array.isArray(cellValue.richText)) {
        return cellValue.richText.map((part) => this.getExcelCellText(part.text)).join('');
      }

      if (cellValue.text !== undefined) {
        return this.getExcelCellText(cellValue.text);
      }

      if (cellValue.result !== undefined) {
        return this.getExcelCellText(cellValue.result);
      }

      if (cellValue.hyperlink !== undefined) {
        return this.getExcelCellText(cellValue.hyperlink);
      }
    }

    return String(value);
  }

  async exportDxGridToExcel(
    component: any,
    options?: {
      worksheetName?: string;
      fileName?: string;
      includeHiddenColumns?: boolean;
      includeHiddenBands?: boolean;
    }
  ): Promise<void> {
    this.dialogService.showMessageSheet("Die Daten werden nach Excel exportiert", 10, false,"bottom", 3000);
	const worksheetName = options?.worksheetName ?? 'Export';
    const fileName = options?.fileName ?? 'export.xlsx';
    const includeHiddenColumns = options?.includeHiddenColumns ?? true;
    const includeHiddenBands = options?.includeHiddenBands ?? true;

    const workbook = new Workbook();
    const worksheet = workbook.addWorksheet(worksheetName);

    const columnsToRestore: Array<{ id: any; visible: boolean }> = [];
    const allColumns: any[] = component.getController?.('columns')?.getColumns?.() ?? [];

    for (const col of allColumns) {
      const columnId = col?.index ?? col?.name ?? col?.dataField;
      if (columnId === undefined) continue;

      const isBand = !!col?.isBand;
      const isDataColumn = !col?.command && col?.type !== 'buttons' && !isBand;
      const canExport = col?.allowExporting !== false;
      const isHidden = col?.visible === false;

      const shouldShowBand = includeHiddenBands && isBand;
      const shouldShowDataColumn = includeHiddenColumns && isDataColumn && canExport;

      if (isHidden && (shouldShowBand || shouldShowDataColumn)) {
        columnsToRestore.push({ id: columnId, visible: false });
        component.columnOption(columnId, 'visible', true);
		
      }
    }

    try {
      await exportDataGrid({
        component,
        worksheet,
        autoFilterEnabled: true
      });

      // Optimize column widths
      worksheet.columns.forEach((column) => {
        let maxLength = 0;
        column.eachCell?.((cell) => {
          const cellLength = this.getExcelCellText(cell.value).length;
          maxLength = Math.max(maxLength, cellLength);
        });
        column.width = Math.min(Math.max(maxLength + 2, 10), 50);
      });

      const buffer = await workbook.xlsx.writeBuffer();
      saveAs(
        new Blob([buffer], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        }),
        fileName
      );
    } finally {
      for (const col of columnsToRestore) {
        component.columnOption(col.id, 'visible', col.visible);
      }
    }
  }
}
